// ============================================================
// FYUNOR — Numeric primitives
// Currency-safe rounding and period conversion. Every financial
// function rounds through these helpers so behaviour is consistent.
// ============================================================

export function isNumeric(value) {
  return typeof value === "number" && Number.isFinite(value);
}

export function safeNumber(value, fallback = 0) {
  const n = typeof value === "string" ? parseFloat(value) : Number(value);
  return Number.isFinite(n) ? n : fallback;
}

export function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

/**
 * Round to `decimals` using the round-half-up convention (financial
 * standard), avoiding binary floating-point drift.
 */
export function round(value, decimals = 2) {
  if (!Number.isFinite(value)) return 0;
  const factor = Math.pow(10, decimals);
  const rounded = Math.round((value + Number.EPSILON) * factor) / factor;
  return rounded;
}

/** Round to a currency's minor-unit precision (default 2 decimals). */
export function roundCurrency(value, decimals = 2) {
  return round(value, decimals);
}

/** Convert an annual nominal rate to a periodic rate. */
export function toPeriodicRate(annualRate, periodsPerYear) {
  return annualRate / periodsPerYear;
}

/** Number of periods for a given number of years. */
export function toPeriods(years, periodsPerYear) {
  return years * periodsPerYear;
}

/** Convert a periodic rate to the equivalent annual nominal rate. */
export function toAnnualRate(periodicRate, periodsPerYear) {
  return periodicRate * periodsPerYear;
}

/** Effective annual rate from a nominal rate compounded m times per year. */
export function effectiveAnnualRate(nominalRate, periodsPerYear) {
  return Math.pow(1 + nominalRate / periodsPerYear, periodsPerYear) - 1;
}

export const PERIODS = {
  ANNUALLY: 1,
  SEMI_ANNUALLY: 2,
  QUARTERLY: 4,
  MONTHLY: 12,
  BI_WEEKLY: 26,
  WEEKLY: 52,
  DAILY: 365,
};